import { request } from './client';

// A provider can have two independent payout wallets. Never infer a missing
// contract balance from the top-level (legacy PRIMARY-only) balance.
const CONTRACTS = [
  { contractKey: 'PRIMARY', contractName: '파주' },
  { contractKey: 'SECONDARY', contractName: '서울' },
];

const emptyContract = ({ contractKey, contractName }) => ({
  contractKey, contractName, status: 'unavailable', balance: null, checkedAt: null,
});

function normalizeContract(definition, rows) {
  const matches = rows.filter((row) => row && row.contractKey === definition.contractKey);
  // A missing or duplicate key cannot reliably identify a wallet.
  if (matches.length !== 1) return emptyContract(definition);
  const row = matches[0];
  const name = typeof row.contractName === 'string' && row.contractName.trim()
    ? row.contractName.trim() : definition.contractName;
  const checkedAt = typeof row.checkedAt === 'string' ? row.checkedAt : null;
  const result = { contractKey: definition.contractKey, contractName: name, status: 'unavailable', balance: null, checkedAt };

  if (row.configured === false || row.errorCode === 'CONTRACT_CONFIGURATION_INVALID') {
    return { ...result, status: 'configuration_error' };
  }
  if (row.configured !== true) return result;
  if (row.errorCode === 'BALANCE_LOOKUP_FAILED') return { ...result, status: 'lookup_failed' };
  if (row.errorCode === 'BALANCE_RESPONSE_INVALID') return { ...result, status: 'invalid_response' };
  if (row.providerSuccess !== true || row.errorCode != null) return result;

  const balance = row.balances?.krw;
  if (typeof balance !== 'number' || !Number.isSafeInteger(balance)) {
    return { ...result, status: 'invalid_response' };
  }
  return { ...result, status: 'success', balance };
}

/** Convert only the contract-specific DTO. An absent field means old server. */
export function normalizeUnmeContractBalances(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('잔액 응답을 확인할 수 없습니다.');
  }
  if (!Object.prototype.hasOwnProperty.call(data, 'contracts')) {
    return { supported: false, contracts: [] };
  }
  const rows = Array.isArray(data.contracts) ? data.contracts : [];
  return { supported: true, contracts: CONTRACTS.map((definition) => normalizeContract(definition, rows)) };
}

/** SUPER-only wallet check; callers must enforce tenant/role gating. */
export async function fetchUnmeContractBalances({ signal } = {}) {
  let data;
  try {
    data = await request({
      method: 'GET',
      url: '/api/admin/cp-settlement/payout/balance',
      params: { provider: 'BILLGATE', includeContracts: '1' },
      timeout: 40000,
      signal,
    });
  } catch (_) {
    // Do not surface or retain raw provider replies, credentials, or errors.
    throw new Error('계약별 잔액을 확인하지 못했습니다.');
  }
  return normalizeUnmeContractBalances(data);
}

/** Format a timezone-qualified server timestamp in Korea, not the browser zone. */
export function formatUnmeBalanceCheckedAt(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    return `${parts.year}.${parts.month}.${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
  } catch (_) { return ''; }
}
